import React, { useRef, useEffect } from 'react';
import { BarChart2, Brain, ChevronDown, ChevronUp } from 'lucide-react';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';
import Card from '../../../components/Card';
import EnhancedSurveyResults from '../EnhancedSurveyResults';
import CreativeReviewNew from '../../../components/CreativeReviewNew';
import AmazonSearchResults from '../../../components/AmazonSearchResults';
import ABTestCreativesResults from '../../../components/abtest/ABTestCreativesResults';
import PackagingReviewV2Results from './PackagingReviewV2Results';
import ConceptTestResults from './ConceptTestResults';
import DrivingConceptTestResults from './DrivingConceptTestResults';
import VitaminConceptTestResults from './VitaminConceptTestResults';
import ClaimsTestingResults from './ClaimsTestingResults';
import DrivingConceptTestResults from './DrivingConceptTestResults';
import BarChartComponent from '../../../components/BarChart';
import LineChartComponent from '../../../components/LineChart';
import PieChart from '../../../components/PieChart';
import RadarChart from '../../../components/RadarChart';
import HorizontalBarChart from '../../../components/HorizontalBarChart';
import EnhancedTableView from '../../../components/EnhancedTableView';
import SimulationInputsDropdown from './SimulationInputsDropdown';
import { jsonrepair } from 'jsonrepair';

interface SimulationData {
  id: number;
  source_model: string;
  created_at: string;
  personas: Array<{
    id: number;
    name: string;
    data: any;
  }>;
  segments?: Array<{
    id: number;
    name: string;
  }>;
}

interface ContentData {
  [key: string]: unknown;
  task?: string;
  images?: string[];
  image?: string[];
  image_descriptions?: string[];
}

interface ParsedResponse {
  output: string;
  analysis: string;
  tables: TableData[];
  hasJsonBlock: boolean;
  winner?: number;
  scores?: number[];
}

interface TableData {
  type: 'bar' | 'line' | 'table' | 'pie' | 'radar' | 'horizontal_chart';
  title?: string;
  data: any[];
  xAxis?: string;
  yAxis?: string;
  headers?: string[];
  nameKey: string;
  valueKey: string;
  messages?: { text: string; percentage?: number; score?: number }[];
}

interface SimulationAnalysisPanelProps {
  simulation: SimulationData;
  simulationResponse: string;
  contentData: ContentData;
  isPreparingExport: boolean;
  expandedCards: Record<string, boolean>;
  onToggleCard: (cardName: string) => void;
  isDetailsDropdownOpen: boolean;
  onCloseDropdown: () => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
  onImageClick: (src: string, alt: string) => void;
}

const SimulationAnalysisPanel: React.FC<SimulationAnalysisPanelProps> = ({
  simulation,
  simulationResponse,
  contentData,
  isPreparingExport,
  expandedCards,
  onToggleCard,
  isDetailsDropdownOpen,
  onCloseDropdown,
  dropdownRef,
  onImageClick,
}) => {
  const parseSimulationResponse = (response: string): ParsedResponse => {
    const jsonBlockMatch = response.match(/((\[[^\}]{3,})?\{\s*[^\}\{]{3,}?:.*\}([^\{]+\])?)/);
    if (jsonBlockMatch) {
      try {
        const jsonContent = jsonBlockMatch[1];
        let parsedData = JSON.parse(jsonContent);
        if (Array.isArray(parsedData)) {
          for (const item of parsedData) {
            if (item.output) {
              parsedData = item;
              break;
            }
          }
        }

        if (typeof parsedData.tables === 'string') {
          try {
            parsedData.tables = parsedData.tables
              .replace(/```json/g, '')
              .replace(/```/g, '')
              .replace(/\n/g, '')
              .replace(/\r/g, '');
            parsedData.tables = JSON.parse(jsonrepair(parsedData.tables));
          } catch (error) {
            console.error('Error parsing tables:', error);
          }
        } else if (parsedData.tables && !Array.isArray(parsedData.tables)) {
          parsedData.tables = [parsedData.tables];
        }

        return {
          output: parsedData.output || '',
          analysis: parsedData.analysis || '',
          tables: parsedData.tables || [],
          hasJsonBlock: true,
          winner: parsedData.winner,
          scores: parsedData.scores,
        };
      } catch (error) {
        console.error('Error parsing JSON block:', error);
        return {
          output: response,
          analysis: '',
          tables: [],
          hasJsonBlock: false,
          winner: undefined,
          scores: undefined,
        };
      }
    } else {
      try {
        let parsedData = JSON.parse(jsonrepair(response));
        if (Array.isArray(parsedData)) {
          for (const item of parsedData) {
            if (item.output) {
              parsedData = item;
              break;
            }
          }
        }

        if (typeof parsedData.tables === 'string') {
          try {
            parsedData.tables = parsedData.tables
              .replace(/```json/g, '')
              .replace(/```/g, '')
              .replace(/\n/g, '')
              .replace(/\r/g, '');
            parsedData.tables = JSON.parse(jsonrepair(parsedData.tables));
          } catch (error) {
            console.error('Error parsing tables:', error);
          }
        } else if (Array.isArray(parsedData.tables)) {
          parsedData.tables = parsedData.tables.map((table: any) => {
            if (typeof table === 'string') {
              table = table
                .replace(/```json/g, '')
                .replace(/```/g, '')
                .replace(/\n/g, '')
                .replace(/\r/g, '');
              return JSON.parse(jsonrepair(table));
            }
            return table;
          });
        } else if (parsedData.tables && !Array.isArray(parsedData.tables)) {
          parsedData.tables = [parsedData.tables];
        }

        return {
          output: parsedData.output || '',
          analysis: parsedData.analysis || '',
          tables: parsedData.tables || [],
          hasJsonBlock: true,
          winner: parsedData.winner,
          scores: parsedData.scores,
        };
      } catch (error) {
        console.error('Error parsing JSON block:', error);
        return {
          output: response,
          analysis: '',
          tables: [],
          hasJsonBlock: false,
          winner: undefined,
          scores: undefined,
        };
      }
    }
  };

  const renderTables = (tables: TableData[], contentData?: ContentData): React.ReactElement | null => {
    if (!tables || tables.length === 0) return null;

    const convertDataToNumbers = (data: any[]): any[] => {
      if (!data) return [];
      return data.map((item) => {
        const convertedItem: Record<string, any> = {};
        Object.entries(item).forEach(([key, value]) => {
          if (typeof value === 'string' && !isNaN(Number(value))) {
            convertedItem[key] = Number(value);
          } else if (typeof value === 'number') {
            convertedItem[key] = value;
          } else {
            convertedItem[key] = value;
          }
        });
        return convertedItem;
      });
    };

    return (
      <div className="mt-6 space-y-6 mb-3">
        {tables.map((table: TableData, index: number) => {
          const formattedData = table.type !== 'horizontal_chart' ? convertDataToNumbers(table.data) : [];

          return (
            <div
              key={index}
              className="bg-white m-3 rounded-lg border border-gray-200 shadow-sm overflow-hidden"
            >
              {table.type === 'horizontal_chart' && table.messages && (
                <div className="p-4">
                  <HorizontalBarChart
                    data={table.messages.map((msg) => ({
                      name: msg.text,
                      value: msg.score || msg.percentage || 0,
                    }))}
                    title={table.title}
                  />
                </div>
              )}

              {table.type === 'bar' && table.data && table.xAxis && table.yAxis && (
                <div className="px-4 py-1 mb-10">
                  <div className="h-96 mb-16">
                    <BarChartComponent
                      data={formattedData}
                      xAxis={table.xAxis}
                      yAxis={table.yAxis}
                      title={table.title}
                    />
                  </div>
                </div>
              )}

              {table.type === 'line' && table.data && table.xAxis && table.yAxis && (
                <div className="p-4">
                  <div className="h-64">
                    <LineChartComponent
                      data={formattedData}
                      xAxis={table.xAxis}
                      yAxis={table.yAxis}
                      title={table.title}
                    />
                  </div>
                </div>
              )}

              {table.type === 'pie' && table.data && table.nameKey && table.valueKey && (
                <div className="p-4">
                  <div className="h-64">
                    <PieChart
                      data={formattedData}
                      nameKey={table.nameKey}
                      valueKey={table.valueKey}
                      title={table.title}
                    />
                  </div>
                </div>
              )}

              {table.type === 'radar' && table.data && table.nameKey && (
                <div className="p-4">
                  <div className="h-64">
                    <RadarChart
                      data={formattedData}
                      nameKey={table.nameKey}
                      title={table.title}
                    />
                  </div>
                </div>
              )}

              {table.type === 'table' && table.data && table.headers && (() => {
                const images = contentData?.images || contentData?.image || [];

                return (
                  <EnhancedTableView
                    headers={table.headers}
                    data={table.data}
                    title={table.title}
                    pdfMode={false}
                    showExportButtons={true}
                    showFullContent={true}
                    images={images || []}
                    isExporting={isPreparingExport}
                  />
                );
              })()}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSimulationContent = (parsedResponse: ParsedResponse, contentData: ContentData) => {
    const isSurveyTask = contentData?.task === 'survey_and_focus_groups';
    const hasEnhancedFormat =
      simulationResponse &&
      (simulationResponse.includes('survey_results') ||
        simulationResponse.includes('"total_questions"') ||
        simulationResponse.includes('"question_id"'));

    if (isSurveyTask && hasEnhancedFormat) {
      try {
        const surveyData = JSON.parse(simulationResponse);
        if (surveyData.survey_results) {
          return (
            <div className="p-6">
              <EnhancedSurveyResults
                data={surveyData}
                contentType={contentData?.task}
                isExporting={isPreparingExport}
                images={contentData?.images || []}
                questions={contentData?.questions || []}
                onImageClick={onImageClick}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Error parsing survey data:', error);
      }
    }

    const isCreativeReviewTask = contentData?.task === 'creative-review';
    if (isCreativeReviewTask && simulationResponse) {
      try {
        const creativeReviewData = JSON.parse(simulationResponse);
        if (creativeReviewData.json_output || creativeReviewData.markdown_output) {
          const creativeReviewDataWithImages = {
            ...creativeReviewData,
            images: contentData?.image || contentData?.images || [],
            image_descriptions: contentData?.image_descriptions || [],
          };

          return (
            <div className="p-6">
              <CreativeReviewNew
                data={creativeReviewDataWithImages}
                isExporting={isPreparingExport}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse creative review format:', error);
      }
    }

    const isAmazonSearchTask = contentData?.task === 'amazon-search';
    if (isAmazonSearchTask && simulationResponse) {
      try {
        const amazonSearchData = JSON.parse(simulationResponse);
        if (
          amazonSearchData.json_response ||
          amazonSearchData.output ||
          amazonSearchData.analysis
        ) {
          return (
            <div className="p-6">
              <AmazonSearchResults
                data={amazonSearchData}
                contentData={contentData}
                isExporting={isPreparingExport}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse amazon search format:', error);
      }
    }

    const isABTestCreativesTask = contentData?.task === 'channel-event-strategy';
    if (isABTestCreativesTask && simulationResponse) {
      try {
        const abTestData = JSON.parse(simulationResponse);
        if (abTestData.output && abTestData.analysis) {
          const abTestDataWithImages = {
            ...abTestData,
            images: contentData?.image || contentData?.images || [],
            image_descriptions: contentData?.image_descriptions || [],
          };

          return (
            <div className="p-6">
              <ABTestCreativesResults
                data={abTestDataWithImages}
                contentType={contentData?.task}
                isExporting={isPreparingExport}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse AB test creatives format:', error);
      }
    }

    const isPackagingReviewV2Task = contentData?.task === 'packaging-review-v2';
    if (isPackagingReviewV2Task && simulationResponse) {
      try {
        const packagingV2Data = JSON.parse(simulationResponse);
        if (packagingV2Data.final_insights ||
            packagingV2Data.technical_compliance_insights ||
            packagingV2Data.shelf_test_insights ||
            packagingV2Data.persona_reaction_insights) {
          return (
            <div className="p-6">
              <PackagingReviewV2Results
                data={packagingV2Data}
                contentData={contentData}
                isPreparingExport={isPreparingExport}
                onImageClick={onImageClick}
              />
            </div>
          );
        }
      } catch (error) {
        console.error('Failed to parse packaging review v2 format:', error);
      }
    }

    // Driving Concept Test task
    if (contentData?.task === 'driving-concept-test' && simulationResponse) {
      return (
        <div className="p-6">
          <DrivingConceptTestResults
            simulationResponse={simulationResponse}
            contentData={contentData}
            isPreparingExport={isPreparingExport}
          />
        </div>
      );
    }

    // Vitamin Concept Test task
    if (contentData?.task === 'vitamin-concept-test' && simulationResponse) {
      return (
        <div className="p-6">
          <VitaminConceptTestResults
            simulationResponse={simulationResponse}
            contentData={contentData}
            isPreparingExport={isPreparingExport}
          />
        </div>
      );
    }

    // Concept Test task
    const isConceptTestTask = contentData?.task === 'concept-test';
    if (isConceptTestTask && simulationResponse) {
      return (
        <div className="p-6">
          <ConceptTestResults
            simulationResponse={simulationResponse}
            contentData={contentData}
            isPreparingExport={isPreparingExport}
            onImageClick={onImageClick}
          />
        </div>
      );
    }

    // Claims Testing task
    if (contentData?.task === 'claims_testing' && simulationResponse) {
      return (
        <div className="p-6">
          <ClaimsTestingResults
            simulationResponse={simulationResponse}
            contentData={contentData}
          />
        </div>
      );
    }

    // Driving Concept Test task
    const isDrivingConceptTestTask = contentData?.task === 'driving-concept-test';
    if (isDrivingConceptTestTask && simulationResponse) {
      return (
        <div className="p-6">
          <DrivingConceptTestResults
            simulationResponse={simulationResponse}
            contentData={contentData}
            isPreparingExport={isPreparingExport}
            onImageClick={onImageClick}
          />
        </div>
      );
    }

    // Legacy rendering for non-special tasks
    return (
      <div className="p-4">
        {/* Only render non-table charts if present */}
        {parsedResponse.tables &&
          parsedResponse.tables.length > 0 &&
          parsedResponse.tables.some((table: TableData) => table.type !== 'table') &&
          renderTables(
            parsedResponse.tables.filter((table: TableData) => table.type !== 'table')
          )}

        <MarkdownRenderer
          content={parsedResponse.output}
          variant="default"
          bordered={true}
          images={contentData?.images || []}
          isExporting={isPreparingExport}
        />

        {/* Only render table type tables if present */}
        {parsedResponse.tables &&
          parsedResponse.tables.length > 0 &&
          parsedResponse.tables.some((table: TableData) => table.type === 'table') &&
          renderTables(
            parsedResponse.tables.filter((table: TableData) => table.type === 'table'),
            contentData
          )}

        {/* Only render analysis if present */}
        {parsedResponse.hasJsonBlock && parsedResponse.analysis && (
          <div className="mt-6">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => onToggleCard('analysis')}
                className="flex items-center justify-between w-full text-left p-4 bg-gradient-to-r from-gray-50 to-white hover:from-primary-light hover:to-white transition-all duration-200 group"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 mr-3 group-hover:border-primary/20 transition-colors">
                    <Brain className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <span className="text-base font-medium text-text-primary">
                      Detailed Analysis
                    </span>
                    <p className="text-xs text-text-secondary mt-0.5">
                      In-depth insights and recommendations
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-text-secondary font-medium">
                    {expandedCards.analysis ? 'Hide Details' : 'View Details'}
                  </span>
                  {expandedCards.analysis ? (
                    <ChevronUp className="h-4 w-4 text-primary" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-primary" />
                  )}
                </div>
              </button>

              {expandedCards.analysis && (
                <div className="border-t border-gray-100 p-6 bg-white">
                  <MarkdownRenderer
                    content={parsedResponse.analysis}
                    variant="default"
                    bordered={false}
                    images={contentData?.images || []}
                    isExporting={isPreparingExport}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const parsedResponse = parseSimulationResponse(simulationResponse || '');

  return (
    <div id="simulation-analysis-section" className="h-full overflow-auto max-h-[calc(90vh-120px)]">
      <div className="mb-6">
        {/* Render dropdown inside analysis section */}
        {isDetailsDropdownOpen && (
          <div ref={dropdownRef}>
            <SimulationInputsDropdown
              simulation={simulation}
              contentData={contentData}
              isOpen={isDetailsDropdownOpen}
              onClose={onCloseDropdown}
              onImageClick={onImageClick}
              expandedCards={expandedCards}
              onToggleCard={onToggleCard}
            />
          </div>
        )}
        
        <Card className="border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-white">{renderSimulationContent(parsedResponse, contentData)}</div>
        </Card>
      </div>
    </div>
  );
};

export default SimulationAnalysisPanel;

